import { createTheme } from "@mui/material/styles";

/**
 * Material Design theme for the company dashboard.
 * @param {('light'|'dark')} mode - 'dark' or 'light'
 * @param {('ltr'|'rtl')} direction - text direction
 */
export function createDashboardTheme(mode = "dark", direction = "ltr") {
  return createTheme({
    direction,
    palette: {
      mode,
      primary: {
        main: "#1976d2",
        light: "#42a5f5",
        dark: "#1565c0",
      },
      secondary: {
        main: "#9c27b0",
        light: "#ba68c8",
        dark: "#7b1fa2",
      },
      success: { main: "#4caf50" },
      warning: { main: "#ff9800" },
      error: { main: "#f44336" },
      background: {
        default: mode === "dark" ? "#050B16" : "#eeeeee",
        paper: mode === "dark" ? "#13171E" : "#FFFFFF",
        // Elevated/card surface – more prominent than paper, matches dashboard stat cards
        elevated:
          mode === "dark"
            ? "#181818"
            : "#f0f2f5",
      },
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: [
        "Inter",
        "Roboto",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: { fontWeight: 600 },
      h2: { fontWeight: 600 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 500 },
      body2: { fontSize: "0.875rem" },
    },
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            // Border side is set per-layout (left in RTL, right in LTR) via sx
            borderColor:
              mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: "2px 8px",
            "&.Mui-selected": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(25, 118, 210, 0.16)"
                  : "rgba(25, 118, 210, 0.08)",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow:
              mode === "dark"
                ? "0 4px 12px rgba(0,0,0,0.3)"
                : "0 2px 8px rgba(0,0,0,0.08)",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          size: "medium",
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
            height: 40,
            minHeight: 40,
            padding: "0 16px",
            boxSizing: "border-box",
            fontSize: "0.875rem",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            "& .MuiButton-startIcon": {
              paddingLeft: 0,
              paddingRight: 0,
              margin: 0,
            },
            "& .MuiButton-endIcon": {
              paddingLeft: 0,
              paddingRight: 0,
              margin: 0,
            },
          },
        },
      },
      /* ToggleButtonGroup – חל על כל הכפתורים בדשבורד (סטטיסטיקות, חוזים, Webhooks, שימוש). גובה כמו ה-Inputs (40px). */
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            height: 40,
            minHeight: 40,
            alignItems: "stretch",
            "& .MuiToggleButton-root": {
              height: "100%",
              minHeight: 40,
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            height: 40,
            minHeight: 40,
            boxSizing: "border-box",
            "&.MuiToggleButton-sizeSmall": {
              height: 40,
              minHeight: 40,
              padding: "0 12px",
            },
            "&:not(.Mui-selected)": {
              backgroundColor: theme.palette.background.paper,
              borderColor: theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.18)"
                : "rgba(0, 0, 0, 0.2)",
              borderWidth: 1,
              borderStyle: "solid",
            },
          }),
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            "& .MuiButton-root": {
              height: 32,
              minHeight: 32,
              padding: "0 10px",
              fontSize: "0.8125rem",
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: "small",
          variant: "outlined",
          margin: "dense",
        },
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiInputBase-input": {
              fontSize: "0.875rem",
            },
            // בעברית: כשאין label – מסגרת אחידה (בלי חור). כשיש label – ריווח ל-notch.
            ...(theme.direction === "rtl"
              ? {
                  "&:not(:has(.MuiInputLabel)) .MuiOutlinedInput .notchedOutline legend": {
                    display: "none",
                  },
                  "&:has(.MuiInputLabel) .MuiOutlinedInput .notchedOutline legend > span": {
                    paddingLeft: 5,
                    paddingRight: 5,
                  },
                }
              : {}),
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) =>
            theme.direction === "rtl"
              ? {
                  right: 8,
                  left: "auto",
                  textAlign: "right",
                  transformOrigin: "top right",
                  "&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
                    transform: "translate(0, 16px) scale(1)",
                  },
                  "&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)": {
                    transform: "translate(-5px, 9px) scale(1)",
                  },
                  "&.MuiInputLabel-outlined.MuiInputLabel-shrink": {
                    transform: "translate(0, -9px) scale(0.75)",
                  },
                  "&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall.MuiInputLabel-shrink": {
                    transform: "translate(-6px, -9px) scale(0.75)",
                  },
                }
              : {},
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: "small",
          margin: "dense",
        },
        styleOverrides: {
          root: ({ theme }) =>
            theme.direction === "rtl"
              ? {
                  "&:not(:has(.MuiInputLabel)) .MuiOutlinedInput .notchedOutline legend": {
                    display: "none",
                  },
                  "&:has(.MuiInputLabel) .MuiOutlinedInput .notchedOutline legend > span": {
                    paddingLeft: 5,
                    paddingRight: 5,
                  },
                }
              : {},
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiInputBase-input": {
              direction: theme.direction === "rtl" ? "rtl" : "ltr",
            },
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: ({ theme }) =>
            theme.direction === "rtl"
              ? {
                  flexDirection: "row-reverse",
                  "& .MuiSelect-select": {
                    paddingRight: 14,
                    paddingLeft: 32,
                  },
                  "& .MuiSelect-icon": {
                    right: "auto",
                    left: 12,
                  },
                }
              : {},
          icon: ({ theme }) =>
            theme.direction === "rtl"
              ? {
                  right: "auto",
                  left: 12,
                }
              : {},
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&.MuiInputBase-sizeSmall": {
              height: 40,
              minHeight: 40,
              boxSizing: "border-box",
            },
          },
          notchedOutline: ({ theme }) =>
            theme.direction === "rtl"
              ? {
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  "& legend": {
                    textAlign: "right",
                  },
                  // padding ל-legend span רק כשיש label – מוסיף ב-MuiTextField/MuiFormControl
                }
              : {},
        },
      },
      MuiIconButton: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            padding: 6,
            "&.MuiIconButton-sizeSmall": {
              padding: 4,
            },
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          color: "text.primary",
        },
        styleOverrides: {
          h1: ({ theme }) => ({ color: theme.palette.text.primary }),
          h2: ({ theme }) => ({ color: theme.palette.text.primary }),
          h3: ({ theme }) => ({ color: theme.palette.text.primary }),
          h4: ({ theme }) => ({ color: theme.palette.text.primary }),
          h5: ({ theme }) => ({ color: theme.palette.text.primary }),
          h6: ({ theme }) => ({ color: theme.palette.text.primary }),
          subtitle1: ({ theme }) => ({ color: theme.palette.text.primary }),
          subtitle2: ({ theme }) => ({ color: theme.palette.text.primary }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
          },
          flexContainer: {
            gap: mode === "dark" ? 28 : 24,
          },
          indicator: {
            display: "none",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => {
            const isDark = theme.palette.mode === "dark";
            return {
              textTransform: "none",
              minHeight: 40,
              minWidth: "auto",
              padding: "0 8px",
              color: isDark ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
              fontSize: "0.9375rem",
              fontWeight: 400,
              "&.Mui-selected": {
                color: theme.palette.primary.main,
                fontWeight: 500,
                borderBottom: "2px solid",
                borderColor: theme.palette.primary.main,
                paddingBottom: 2,
              },
            };
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            margin: "75px auto 48px auto",
            backgroundColor:
              mode === "dark" ? "#1c2128" : "#ffffff",
            backgroundImage: "none",
            border: `1px solid ${mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
            boxShadow:
              mode === "dark"
                ? "0 12px 40px rgba(0,0,0,0.4)"
                : "0 12px 40px rgba(0,0,0,0.12)",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            textAlign: "center",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            justifyContent: "flex-end",
            flexDirection: "row",
            direction: "ltr",
          },
        },
      },
    },
  });
}
